import scriptjs from 'scriptjs';
import {autobind} from 'core-decorators';
import {getDOMNode, getDOMNodes} from 'utils';
import BaseAdapter from './base';

export const loadScript = scriptjs;

// https://github.com/vimeo/player.js

@autobind
export default class VimeoAdapter extends BaseAdapter {
    apiUrls = {
        data: 'https://api.vimeo.com',
        oauth: 'https://api.vimeo.com/oauth/authorize/client',
        player: 'https://player.vimeo.com/api/player.js',
    }

    static findVideos() {
        const nodes = getDOMNodes('iframe[src*="player.vimeo.com/video/"]');
        return nodes.map(iframe => ({
            provider: 'vimeo',
            videoId: iframe.src.match(/video\/([\w-]+)/)[1],
            videoNode: iframe,
        }));
    }

    loadAPI() {
        return new Promise((resolve, reject) => {
            const {player: playerUrl} = this.apiUrls;
            const check = () => {
                if (window.Vimeo) resolve();
                else setTimeout(check, BaseAdapter.CHECK_INTERVAL);
            };

            if (!getDOMNode(`script[src="${playerUrl}"]`)) exports.loadScript(playerUrl, check);
            else check();

            const timeout = () => reject('Vimeo Player API failed to load');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    attach() {
        this.api = new window.Vimeo.Player(this.videoNode);
        this.api.on('pause', () => this.setPlayerState(BaseAdapter.STATES.Paused));
        this.api.on('play', () => this.setPlayerState(BaseAdapter.STATES.Playing));
        this.api.on('seeked', () => this.setPlayerState(BaseAdapter.STATES.Seek));
        this.api.on('ended', () => this.setPlayerState(BaseAdapter.STATES.Ended));
        this.api.on('timeupdate', this.syncPlayhead);

        // Use 'loaded' to kick-off wrapper so we're sure we have the metadata need for the wrapper to work.
        // This way we also handle password protected videos.
        this.api.on('loaded', this.handleReady);
    }

    handleReady() {
        this.getDuration().then(this.setDuration);
        this.api.disableTextTrack();
    }

    syncPlayhead(payload) {
        const currentTime = this.toMS(payload.seconds);
        this.setPlayhead(currentTime);
    }

    seek(time) {
        this.api.setCurrentTime(this.toSS(time));
    }

    pause() {
        this.api.pause();
    }

    getDuration() {
        return this.api.getDuration().then(this.toMS);
    }

    getCurrentTime() {
        return this.api.getCurrentTime().then(this.toMS);
    }

    getVideoTitle() {
        return this.api.getVideoTitle();
    }

    async getVideoData() {
        const {appId, appSecret} = VWRAP.vimeoApiConfig;
        let response;
        try {
            const authRequestBody = JSON.stringify({
                grant_type: 'client_credentials',
                scope: 'public',
            });

            const encodedAuthorization = btoa(`${appId}:${appSecret}`);
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `basic ${encodedAuthorization}`,
                Accept: 'application/vnd.vimeo.*+json;version=3.4',
            };

            const oauthResponse = await fetch(this.apiUrls.oauth, {
                method: 'POST',
                body: authRequestBody,
                headers,
            });

            if (oauthResponse.status >= 400) return null;

            const oauthCredentials = await oauthResponse.json();
            const {access_token: accessToken} = oauthCredentials;

            const endpoint = `${this.apiUrls.data}/videos/${this.videoId}?access_token=${accessToken}`;

            response = await fetch(endpoint);
        } catch (error) {
            console.error(error); // eslint-disable-line no-console
            return null;
        }

        if (response.status >= 400) return null;

        return response.json();
    }

    async getMetadata() {
        const videoData = await this.getVideoData();

        if (!videoData) return {};

        const {release_time, link: contentUrl, name, pictures} = videoData;

        const description = videoData.description || name;
        const uploadDate = new Date(release_time).toISOString();
        const thumbnailUrl = pictures.sizes[Math.floor(pictures.sizes.length / 2)].link;

        return {name, description, contentUrl, uploadDate, thumbnailUrl};
    }
}



// WEBPACK FOOTER //
// ./scripts/adapters/vimeo.js